/**
 * Fixture de DEV — usado só quando REACT_APP_TICKETS_API_URL não está setado
 * (dev local), espelhando o idiom `isMock` do useUnifiedView. Não é produção.
 */
import {
  Ticket,
  TicketEvent,
  TicketDetailResponse,
  TicketCountsResponse,
} from './models/Ticket';

const iso = (daysFromNow: number) =>
  new Date(Date.now() + daysFromNow * 86_400_000).toISOString();

const base = (
  over: Partial<Ticket> &
    Pick<Ticket, 'uid' | 'reference_id' | 'title' | 'status'>
): Ticket => ({
  description: '',
  priority: 'media',
  channel: 'whatsapp',
  category: 'duvida',
  nature: 'corretivo',
  segment: 'ouro',
  account_uid: null,
  account_name: null,
  contact_name: null,
  contact_phone: null,
  order_uid: null,
  order_reference_id: null,
  order_value: null,
  assignee_uid: null,
  assignee_name: null,
  tags: [],
  sla_first_response_at: null,
  sla_resolution_at: null,
  first_responded_at: null,
  auto_close_at: null,
  resolved_at: null,
  closed_at: null,
  last_response_at: null,
  last_response_by: null,
  response_due_at: null,
  created_at: iso(-1),
  updated_at: iso(-0.2),
  ...over,
});

export const FIXTURE_TICKETS: Ticket[] = [
  base({
    uid: 'fx-1',
    reference_id: 4821,
    title: 'Peça divergente do pedido — chegou pastilha errada',
    category: 'entrega_incorreta_incompleta',
    last_response_at: iso(-0.005),
    last_response_by: 'ops',
    response_due_at: iso(0.005),
    description:
      'Oficina recebeu pastilha de freio do modelo errado. Cliente pede troca urgente.',
    status: 'aberto',
    priority: 'alta',
    channel: 'whatsapp',
    segment: 'ouro',
    account_uid: 'acc-1',
    account_name: 'Auto Center Vila Mariana',
    order_uid: 'ord-1',
    order_reference_id: 'PED-90231',
    order_value: 1500,
    tags: ['troca', 'divergência'],
    sla_first_response_at: iso(0.1),
    sla_resolution_at: iso(0.15),
    updated_at: iso(-0.005),
  }),
  base({
    uid: 'fx-2',
    reference_id: 4820,
    title: 'Cliente não recebeu nota fiscal do pedido',
    description:
      'Pedido entregue mas NF-e não chegou no e-mail. Solicitou reenvio.',
    status: 'aberto',
    priority: 'baixa',
    channel: 'email',
    category: 'pagamento',
    segment: 'prata',
    account_uid: 'acc-2',
    account_name: 'Mecânica do Zé',
    order_uid: 'ord-2',
    order_reference_id: 'PED-90188',
    order_value: 320,
    tags: ['fiscal'],
    sla_resolution_at: iso(0.9),
  }),
  base({
    uid: 'fx-3',
    reference_id: 4818,
    title: 'Atraso na entrega expressa — prometido para hoje',
    category: 'atraso_rota',
    // Nunca respondido: o relógio corre desde created_at e já estourou.
    response_due_at: iso(-0.02),
    description:
      'Entrega expressa contratada para o mesmo dia, transportadora marcou como atrasada.',
    status: 'em_andamento',
    priority: 'urgente',
    channel: 'ligacao',
    segment: 'diamante',
    account_uid: 'acc-3',
    account_name: 'Garagem Premium Motors',
    order_uid: 'ord-3',
    order_reference_id: 'PED-90155',
    order_value: 2890,
    assignee_uid: 'mem-1',
    assignee_name: 'Marina Alves',
    tags: ['logística', 'expresso'],
    sla_resolution_at: iso(0.04),
  }),
  base({
    uid: 'fx-4',
    reference_id: 4810,
    title: 'Reembolso pendente de item cancelado',
    description:
      'Item cancelado há 5 dias, reembolso ainda não caiu. Cliente pede posição.',
    status: 'aguardando_cliente',
    priority: 'media',
    channel: 'marketplace_ml',
    category: 'pagamento',
    segment: 'marketplace',
    account_uid: 'acc-4',
    account_name: 'Auto Peças Brasil',
    order_uid: 'ord-4',
    order_reference_id: 'PED-89901',
    order_value: 640,
    assignee_uid: 'mem-2',
    assignee_name: 'Rafael Souza',
    tags: ['financeiro', 'reembolso'],
    sla_resolution_at: iso(-0.2),
  }),
  base({
    uid: 'fx-5',
    reference_id: 4807,
    title: 'Lead sem conta pedindo cotação por telefone',
    nature: 'preventivo',
    description:
      'Contato avulso (sem cadastro) querendo cotar jogo de amortecedores.',
    status: 'em_andamento',
    priority: 'baixa',
    channel: 'ligacao',
    category: 'duvida',
    segment: 'select',
    contact_name: 'João (lead)',
    contact_phone: '(11) 95555-1234',
    assignee_uid: 'mem-1',
    assignee_name: 'Marina Alves',
    tags: ['pré-venda'],
  }),
  base({
    uid: 'fx-6',
    reference_id: 4795,
    title: 'Troca aprovada e nova peça despachada',
    description:
      'Peça correta enviada, código de rastreio compartilhado com a oficina.',
    status: 'resolvido',
    priority: 'media',
    channel: 'whatsapp',
    segment: 'ouro',
    account_uid: 'acc-1',
    account_name: 'Auto Center Vila Mariana',
    order_uid: 'ord-6',
    order_reference_id: 'PED-89701',
    order_value: 1500,
    assignee_uid: 'mem-3',
    assignee_name: 'Camila Dias',
    tags: ['troca'],
    first_responded_at: iso(-1.8),
    resolved_at: iso(-0.5),
    auto_close_at: iso(0.5),
    updated_at: iso(-0.5),
  }),
  base({
    uid: 'fx-7',
    reference_id: 4790,
    title: 'NF-e reenviada e confirmada — ticket encerrado',
    description: 'Nota reenviada, cliente confirmou recebimento.',
    status: 'fechado',
    priority: 'baixa',
    channel: 'email',
    category: 'pagamento',
    segment: 'prata',
    account_uid: 'acc-2',
    account_name: 'Mecânica do Zé',
    order_uid: 'ord-7',
    order_reference_id: 'PED-89688',
    order_value: 210,
    assignee_uid: 'mem-3',
    assignee_name: 'Camila Dias',
    tags: ['fiscal'],
    first_responded_at: iso(-3),
    resolved_at: iso(-2),
    closed_at: iso(-1),
    updated_at: iso(-1),
  }),
];

export const FIXTURE_EVENTS: Record<string, TicketEvent[]> = {
  'fx-1': [
    {
      uid: 'ev-1c',
      ticket_uid: 'fx-1',
      type: 'created',
      actor_uid: null,
      actor_name: null,
      body: null,
      metadata: {},
      created_at: iso(-0.01),
    },
  ],
  'fx-6': [
    {
      uid: 'ev-6r',
      ticket_uid: 'fx-6',
      type: 'status_changed',
      actor_uid: 'mem-3',
      actor_name: 'Camila Dias',
      body: null,
      metadata: {from: 'em_andamento', to: 'resolvido'},
      created_at: iso(-0.5),
    },
    {
      uid: 'ev-6m',
      ticket_uid: 'fx-6',
      type: 'comment',
      actor_uid: 'mem-3',
      actor_name: 'Camila Dias',
      body: 'Peça correta despachada, rastreio enviado à oficina.',
      metadata: {},
      created_at: iso(-1.7),
    },
    {
      uid: 'ev-6c',
      ticket_uid: 'fx-6',
      type: 'created',
      actor_uid: null,
      actor_name: null,
      body: null,
      metadata: {},
      created_at: iso(-2),
    },
  ],
};

export const fixtureCounts = (): TicketCountsResponse => {
  const by_status = {
    aberto: 0,
    em_andamento: 0,
    aguardando_cliente: 0,
    resolvido: 0,
    fechado: 0,
  } as TicketCountsResponse['by_status'];
  FIXTURE_TICKETS.forEach((t) => (by_status[t.status] += 1));
  const open_total =
    by_status.aberto +
    by_status.em_andamento +
    by_status.aguardando_cliente +
    by_status.resolvido;
  return {by_status, open_total};
};

export const fixtureDetail = (uid: string): TicketDetailResponse => {
  const ticket =
    FIXTURE_TICKETS.find((t) => t.uid === uid) ?? FIXTURE_TICKETS[0];
  return {ticket, events: FIXTURE_EVENTS[ticket.uid] ?? []};
};
